import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { orientedFace, planPoint } from '../solids';
import { buildVesselRoof } from './churchVessel';
import { buttress, centredWindow, gableWall, pointedWindows, rectWall } from './masonry';
import { meshFrameOf } from './planFrame';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { sintPieterskapelPlan } from './sintPieterskapelPlan';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** Pieters' own lower, flat-gabled nave, separate from Margriet and the hospital wards. */
export function buildSintPieterskapel(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors,
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY
): void {
  const { frame: plan, vessel: v, front, sides, buttresses: b } = sintPieterskapelPlan(landmark),
    frame = meshFrameOf(plan),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    width = v.half * 2,
    lo = v.vc - v.half,
    hi = v.vc + v.half,
    heights = { eaveY: v.eaveY, apexY: v.ridgeY },
    portal = centredWindow(width, front.portalSill, front.portalTop, front.portalShare),
    circle: Ring = Array.from({ length: 16 }, (_, i): Vec2 => {
      const a = (i * Math.PI) / 8;
      return [
        width / 2 + front.oculusRadius * Math.cos(a),
        front.oculusHeight + front.oculusRadius * Math.sin(a),
      ];
    });
  gableWall(
    builder,
    brick,
    frame,
    [
      [v.u0, hi],
      [v.u0, lo],
    ],
    heights,
    [
      ...portal,
      ...centredWindow(width, front.windowSill, front.windowTop, front.windowShare),
      circle,
    ]
  );
  // A closed timber leaf is recessed within the portal. The source gives no interior plan.
  for (const ring of portal) {
    const wood = { ...brick.opening, surface: SURFACE.wood, pattern: PATTERN.timberFront1538 },
      face = ring.map((p) => planPoint(v.u0 + 0.12, p[1], hi - p[0])),
      back = ring.map((p) => planPoint(v.u0 + 0.17, p[1], hi - p[0]));
    orientedFace(builder, wood, frame, face, [-1, 0, 0]);
    orientedFace(builder, wood, frame, back, [1, 0, 0]);
    ring.forEach((p, i) => {
      const next = (i + 1) % ring.length,
        q = ring[next]!;
      orientedFace(
        builder,
        wood,
        frame,
        [face[i]!, face[next]!, back[next]!, back[i]!],
        [0, p[0] - q[0], q[1] - p[1]]
      );
    });
  }
  gableWall(
    builder,
    brick,
    frame,
    [
      [v.chordU, lo],
      [v.chordU, hi],
    ],
    heights,
    centredWindow(width, sides.windowSill, sides.windowTop, 0.3)
  );
  for (const side of [-1, 1]) {
    const vc = v.vc + side * v.half,
      from: Vec2 = side < 0 ? [v.u0, vc] : [v.chordU, vc],
      to: Vec2 = side < 0 ? [v.chordU, vc] : [v.u0, vc];
    rectWall(
      builder,
      brick,
      frame,
      [from, to],
      [0, v.eaveY],
      pointedWindows(v.chordU - v.u0, sides.bay, sides.windowSill, sides.windowTop)
    );
    for (const u of b.centres)
      buttress(builder, brick, frame, [u, vc], [0, side], { ...b, pinnacle: 0 });
  }
  buildVesselRoof(builder, brick, frame, v);
}
